import { useState } from 'react';
import { Waypoints, RefreshCw, Plus, Pencil, GitCompare, ShieldCheck } from 'lucide-react';
import { api } from '@/data/api';
import {
  NPM_MANAGED_STATES, npmApiErrorMessage, npmConnectionState, npmCredentialsPayload, npmErrorSummary, npmSslLabel, npmSslOptions, npmSyncMessage,
} from '@/data/npmState';
import { useAsync } from '@/hooks/useAsync';
import { PageHeader, DataTable, AsyncBoundary, RowsSkeleton, EmptyState } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const inputClass = 'w-full rounded border border-line bg-background px-2 py-1.5 text-sm';
const fmt = (value) => (value ? new Date(value).toLocaleString('pt-BR') : null);

function Field({ label, hint, children }) {
  return <label className="block space-y-1 text-xs"><span className="text-muted-foreground">{label}</span>{children}{hint && <span className="block text-muted-foreground">{hint}</span>}</label>;
}

function Alert({ tone = 'err', children }) {
  const cls = tone === 'ok' ? 'border-ok/30 bg-ok/10 text-ok' : 'border-err/30 bg-err/10 text-err';
  return <p role="alert" className={`rounded-lg border px-3 py-2 text-sm ${cls}`}>{children}</p>;
}

// Configurar (novo) ou Editar integração (existente). A senha armazenada nunca
// é carregada; campo vazio na edição = manter a atual.
function IntegrationDialog({ target, onClose, onSaved }) {
  const editing = !!target?.config?.configured;
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (event) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.configureNpm(target.server.id, npmCredentialsPayload({
        base_url: form.get('base_url'), identity: form.get('identity'), secret: form.get('secret'),
      }));
      formEl.querySelector('[name=secret]').value = '';
      setResult(saved.validation.success
        ? { ok: true, text: 'Credencial salva e validada no NPM (POST /api/tokens). Clique em Sincronizar para descobrir os Proxy Hosts.' }
        : { ok: false, text: `Credencial salva, mas a validação falhou: ${npmErrorSummary(saved.validation.error)}` });
      onSaved();
    } catch (error) {
      setResult({ ok: false, text: npmApiErrorMessage(error) });
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-lg border-line bg-popover">
      <DialogHeader>
        <DialogTitle>{editing ? 'Editar integração' : 'Configurar integração'} · {target?.server.hostname}</DialogTitle>
        <DialogDescription>A senha é armazenada criptografada e nunca é exibida. Após salvar, a TintiCloud valida a credencial apenas com POST /api/tokens; o token não é persistido.</DialogDescription>
      </DialogHeader>
      {target && <form className="space-y-3" onSubmit={submit} data-testid="npm-integration-form">
        <Field label="URL do NPM (origem, sem /api)">
          <input name="base_url" type="url" required defaultValue={target.config?.base_url || ''} placeholder="http://nginx-proxy-manager:81" className={inputClass} />
        </Field>
        <Field label="Identity (e-mail do usuário NPM)">
          <input name="identity" type="email" required={!editing} defaultValue={target.config?.identity || ''} placeholder="admin@example.com" autoComplete="off" className={inputClass} />
        </Field>
        <Field label="Senha" hint={editing && target.config?.has_secret ? 'Deixe em branco para manter a senha armazenada.' : null}>
          <input name="secret" type="password" required={!editing} defaultValue="" placeholder={editing ? 'Nova senha (opcional)' : 'Senha'} autoComplete="new-password" className={inputClass} data-testid="npm-secret" />
        </Field>
        {result && <Alert tone={result.ok ? 'ok' : 'err'}>{result.text}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Fechar</Button>
          <Button type="submit" size="sm" disabled={busy}>{busy ? 'Validando…' : 'Salvar e validar'}</Button>
        </div>
      </form>}
    </DialogContent>
  </Dialog>;
}

// Opções que o provider realmente aplica hoje. O resto não é exposto.
const UNSUPPORTED = 'SSL é configurado depois, na ação "Configurar SSL" do host. Websockets, Block Common Exploits, Cache Assets e Access List ainda não são suportados pelo provider da TintiCloud; configure-os no NPM após a criação, se necessário.';

// Novo Proxy Host (host = null) ou editar um host gerenciado (host = linha da tabela).
function ProxyHostDialog({ open, host, servers, onClose, onSaved }) {
  const editing = !!host;
  const [serverId, setServerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const current = editing ? host.serverId : (serverId || servers[0]?.id || '');
  const containers = useAsync(() => (open && current ? api.getNpmContainers(current) : Promise.resolve([])), [open, current]);
  const sites = useAsync(() => (open && !editing ? api.getSites() : Promise.resolve([])), [open, editing]);
  const candidates = (containers.data || []).filter((c) => c.project_id && ['adopted', 'managed'].includes(c.adoption_state));
  const submit = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const body = { forward_host: form.get('forward_host'), forward_port: Number(form.get('forward_port')) };
    setBusy(true);
    setError(null);
    try {
      if (editing) await api.updateNpmProxyHost(host.serverId, host.id, body);
      else await api.createNpmProxyHost(current, { ...body, domain: form.get('domain') });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.status === 409 && /not managed|não é gerenciado/i.test(npmApiErrorMessage(e))
        ? 'Já existe um Proxy Host para este domínio no NPM que não foi criado pela TintiCloud. Ele não será apropriado nem alterado automaticamente.'
        : npmApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg border-line bg-popover">
      <DialogHeader>
        <DialogTitle>{editing ? `Editar Proxy Host · ${host.domain}` : 'Novo Proxy Host'}</DialogTitle>
        <DialogDescription>Fluxo gerenciado: Site → Projeto → Container → NPM. O domínio precisa ser o domínio de um Site gerenciado e o destino, um container adotado do mesmo projeto. Requer permissão npm.write no projeto.</DialogDescription>
      </DialogHeader>
      {open && <form className="space-y-3" onSubmit={submit} data-testid="npm-proxy-form">
        {!editing && <Field label="Servidor">
          <select value={current} onChange={(e) => setServerId(e.target.value)} className={inputClass}>
            {servers.map((s) => <option key={s.id} value={s.id}>{s.hostname}</option>)}
          </select>
        </Field>}
        {!editing && <Field label="Domain Name" hint="Um domínio por Proxy Host (domínio primário do Site).">
          <input name="domain" required list="npm-site-domains" placeholder="app.example.com" className={inputClass} />
          <datalist id="npm-site-domains">{(sites.data || []).filter((s) => s.primaryDomain).map((s) => <option key={s.id} value={s.primaryDomain}>{s.name}</option>)}</datalist>
        </Field>}
        <Field label="Scheme"><input value="http" disabled className={inputClass} readOnly /></Field>
        <Field label="Forward Hostname (container)">
          <input name="forward_host" required list="npm-containers" defaultValue={host?.forward_host || ''} placeholder="nome-do-container" className={inputClass} />
          <datalist id="npm-containers">{candidates.map((c) => <option key={c.id} value={c.name} />)}</datalist>
        </Field>
        <Field label="Forward Port"><input name="forward_port" type="number" min="1" max="65535" required defaultValue={host?.forward_port || 80} className={inputClass} /></Field>
        <p className="text-xs text-muted-foreground">{UNSUPPORTED}</p>
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button type="submit" size="sm" disabled={busy || !current}>{busy ? 'Aplicando…' : editing ? 'Salvar e reconciliar' : 'Criar Proxy Host'}</Button>
        </div>
      </form>}
    </DialogContent>
  </Dialog>;
}

const CERT_ACTIONS = {
  kept: 'Certificado atual mantido (ainda válido) — nenhuma nova emissão.',
  reused: 'Certificado Let\'s Encrypt existente e compatível reutilizado — nenhuma nova emissão.',
  requested: 'Certificado emitido pelo Let\'s Encrypt via NPM.',
};

// SSL somente para hosts gerenciados. Os domínios não são editáveis: o
// backend exige exatamente os domínios gerenciados do host.
function SslDialog({ host, identity, onClose, onSaved }) {
  const [options, setOptions] = useState({ force_ssl: true, http2_support: true, hsts_enabled: false, hsts_subdomains: false });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!host) return null;
  const domains = host.domain_names?.length ? host.domain_names : [host.domain];
  const normalized = npmSslOptions(options);
  const toggle = (key) => setOptions((current) => ({ ...current, [key]: !current[key] }));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const saved = await api.configureNpmProxyHostSsl(host.serverId, host.id, { domain_names: domains, accept_letsencrypt_terms: accepted, ...normalized });
      setResult({ ok: true, text: `${CERT_ACTIONS[saved.certificate_action] || 'SSL aplicado.'} Certificado #${saved.certificate_id} associado e reconciliado.` });
      onSaved();
    } catch (e) {
      setResult({ ok: false, text: e.status === 504
        ? 'O NPM ainda está processando a emissão (pode levar alguns minutos). Aguarde e clique em Sincronizar; repetir a ação reutiliza o certificado, sem emitir outro.'
        : npmApiErrorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  const check = (key, label, disabled = false) => <label className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50' : ''}`}>
    <input type="checkbox" checked={!!normalized[key]} disabled={disabled} onChange={() => toggle(key)} name={key} /> {label}
  </label>;
  return <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg border-line bg-popover">
      <DialogHeader>
        <DialogTitle>Configurar SSL · {host.domain}</DialogTitle>
        <DialogDescription>Se já houver certificado Let's Encrypt válido para exatamente estes domínios, ele é reutilizado; caso contrário o NPM solicita um novo (desafio HTTP — o DNS deve apontar para este servidor e a porta 80 deve estar acessível).</DialogDescription>
      </DialogHeader>
      <form className="space-y-3" onSubmit={submit} data-testid="npm-ssl-form">
        <Field label="Domínios (gerenciados, não editáveis)"><div className="rounded border border-line bg-background px-2 py-1.5 text-sm">{domains.join(', ')}</div></Field>
        <p className="text-xs text-muted-foreground">E-mail da conta Let's Encrypt: o do usuário NPM configurado na integração{identity ? ` (${identity})` : ''}. A API do NPM 2.15 não aceita outro e-mail.</p>
        <div className="grid grid-cols-2 gap-2">
          {check('force_ssl', 'Force SSL')}
          {check('http2_support', 'HTTP/2')}
          {check('hsts_enabled', 'HSTS', !normalized.force_ssl)}
          {check('hsts_subdomains', 'HSTS Subdomains', !normalized.hsts_enabled)}
        </div>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={accepted} onChange={() => setAccepted(!accepted)} name="accept_letsencrypt_terms" />
          <span>Concordo com os <a className="text-brand underline" href="https://letsencrypt.org/repository/" target="_blank" rel="noreferrer">termos do Let's Encrypt</a>.</span></label>
        {result && <Alert tone={result.ok ? 'ok' : 'err'}>{result.text}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Fechar</Button>
          <Button type="submit" size="sm" disabled={busy || !accepted}>{busy ? 'Solicitando… (pode levar até alguns minutos)' : 'Aplicar SSL'}</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
}

function ServerRow({ row, busy, onEdit, onSync }) {
  const { config } = row;
  const state = npmConnectionState(config);
  const configured = !!config?.configured;
  return <div className="rounded-lg border border-line bg-surface px-4 py-3 text-sm" data-testid={`npm-server-${row.server.hostname}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <span className="font-medium">{row.server.hostname}</span>{' · '}
        {configured ? <><span className="text-ok">Configurado</span>{' · '}<span className={state.tone} data-testid="npm-connection-state">{state.label}</span></>
          : <span className="text-muted-foreground">NPM não configurado neste servidor</span>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onEdit} data-testid="npm-edit-integration"><Pencil className="h-3.5 w-3.5" /> {configured ? 'Editar integração' : 'Configurar integração'}</Button>
        {configured && <Button size="sm" variant="outline" disabled={busy} onClick={onSync}><RefreshCw className="h-3.5 w-3.5" /> Sincronizar</Button>}
      </div>
    </div>
    {configured && <div className="mt-1 text-xs text-muted-foreground">
      {config.base_url}{config.identity ? ` · ${config.identity}` : ''}{' · '}Hosts descobertos: {config.host_count}
      {' · '}Última sincronização: {config.last_sync_at ? `${fmt(config.last_sync_at)} (${config.last_sync_count ?? 0} hosts)` : 'nunca concluída'}
    </div>}
    {configured && config.last_error && <div className="mt-1 text-xs text-err">
      Última verificação ({fmt(config.last_check_at)}): {npmErrorSummary(config.last_error)}
    </div>}
    {configured && config.state === 'unverified' && <div className="mt-1 text-xs text-warn">Credencial alterada e ainda não validada.</div>}
    {row.syncError && <div className="mt-1 text-xs text-err">Sincronização falhou agora: {npmSyncMessage(row.syncError)}{row.syncError.stage ? ` · estágio ${row.syncError.stage}` : ''}{row.syncError.http_status ? ` · HTTP ${row.syncError.http_status}` : ''}</div>}
  </div>;
}

export default function NginxProxyManager() {
  const [syncIds, setSyncIds] = useState([]);
  const [version, setVersion] = useState(0);
  const [integration, setIntegration] = useState(null);
  const [proxyDialog, setProxyDialog] = useState(null); // { host: row | null }
  const [sslHost, setSslHost] = useState(null);
  const [actionError, setActionError] = useState(null);
  const q = useAsync(() => api.getNpm(syncIds), [version, syncIds]);
  const reload = () => { setSyncIds([]); setVersion((v) => v + 1); };
  const sync = (ids) => { setSyncIds(ids); setVersion((v) => v + 1); };
  const rows = q.data?.rows || [];
  const configuredIds = rows.filter((r) => r.config?.configured && r.config?.enabled).map((r) => r.server.id);
  const connectedServers = rows.filter((r) => r.config?.state === 'connected').map((r) => r.server);
  const hosts = q.data?.hosts || [];
  const reconcile = async (host) => {
    setActionError(null);
    try {
      await api.reconcileNpmProxyHost(host.serverId, host.id);
      reload();
    } catch (e) {
      setActionError(`Reconcile de ${host.domain} falhou: ${npmApiErrorMessage(e)}`);
    }
  };
  return <div className="space-y-6 animate-fade-up">
    <PageHeader icon={Waypoints} title="Nginx Proxy Manager" description="Proxy hosts reais observados no NPM. A sincronização é somente leitura; alterações só em hosts criados pela TintiCloud."
      actions={<div className="flex gap-2">
        <Button variant="outline" disabled={q.loading || !connectedServers.length} data-testid="npm-new-proxy" onClick={() => setProxyDialog({ host: null })}><Plus className="h-4 w-4" /> Novo Proxy Host</Button>
        <Button disabled={q.loading || !configuredIds.length} data-testid="npm-sync" onClick={() => sync(configuredIds)}><RefreshCw className="h-4 w-4" /> Sincronizar</Button>
      </div>} />
    {actionError && <Alert>{actionError}</Alert>}
    <AsyncBoundary loading={q.loading} error={q.error} skeleton={<RowsSkeleton />}>
      <div className="space-y-2">{rows.map((row) => <ServerRow key={row.server.id} row={row} busy={q.loading}
        onEdit={() => setIntegration(row)} onSync={() => sync([row.server.id])} />)}</div>
      {hosts.length ? <DataTable testid="npm-table" rows={hosts} columns={[
        { key: 'domain', header: 'Domínio', render: (row) => row.domain_names?.join(', ') || row.domain || 'Unknown' }, { key: 'server', header: 'Servidor' }, { key: 'forward_host', header: 'Forward host' }, { key: 'forward_port', header: 'Porta' },
        { key: 'forward_scheme', header: 'Scheme', render: (row) => row.forward_scheme || 'Unknown' }, { key: 'ssl_enabled', header: 'SSL', render: (row) => npmSslLabel(row) },
        { key: 'enabled', header: 'Ativo', render: (row) => row.enabled == null ? 'Unknown' : row.enabled ? 'Sim' : 'Não' },
        { key: 'adoption_state', header: 'Gestão', render: (row) => NPM_MANAGED_STATES.includes(row.adoption_state) ? 'TintiCloud' : row.adoption_state === 'discovered' ? 'Não gerenciado' : row.adoption_state },
        { key: 'missing_since', header: 'Estado', render: (row) => row.missing_since ? 'Ausente'
          : row.drift_detected ? <span className="text-warn" title="Desejado (TintiCloud) × atual (NPM)">Drift{row.actual ? ` · NPM: ${row.actual.forward_host}:${row.actual.forward_port}${row.actual.certificate_id !== row.certificate_id ? ` · cert ${row.actual.certificate_id ?? 'nenhum'}` : ''}` : ''}</span> : 'Atual' },
        { key: 'actions', header: '', render: (row) => NPM_MANAGED_STATES.includes(row.adoption_state) && !row.missing_since ? <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => setProxyDialog({ host: row })}><Pencil className="h-3.5 w-3.5" /> Editar</Button>
          <Button size="sm" variant="ghost" onClick={() => reconcile(row)}><GitCompare className="h-3.5 w-3.5" /> Reconciliar</Button>
          <Button size="sm" variant="ghost" onClick={() => setSslHost(row)}><ShieldCheck className="h-3.5 w-3.5" /> {row.certificate_id ? 'SSL' : 'Configurar SSL'}</Button>
        </div> : row.adoption_state === 'discovered' ? <span className="text-xs text-muted-foreground">Somente leitura</span> : null },
      ]} /> : <EmptyState icon={Waypoints} title={rows.some((r) => r.syncError) ? 'Sincronização do NPM falhou' : 'Nenhum proxy host descoberto'} description={rows.some((r) => r.syncError) ? 'Os hosts locais não foram atualizados nesta tentativa.' : 'Configure o NPM em um servidor e sincronize para consultar dados reais.'} />}
    </AsyncBoundary>
    <SslDialog key={sslHost?.id || 'none'} host={sslHost} identity={rows.find((r) => r.server.id === sslHost?.serverId)?.config?.identity}
      onClose={() => setSslHost(null)} onSaved={reload} />
    <IntegrationDialog target={integration} onClose={() => setIntegration(null)} onSaved={reload} />
    <ProxyHostDialog open={!!proxyDialog} host={proxyDialog?.host || null} servers={connectedServers}
      onClose={() => setProxyDialog(null)} onSaved={reload} />
  </div>;
}
